import { isLoading } from "apollo/utils";
import {
  BACKEND,
  QUERY_PROGRESS_FAILED,
  QUERY_PROGRESS_NOT_STARTED,
  QUERY_PROGRESS_PENDING,
  QUERY_PROGRESS_SUCCEED,
} from "core/consts";
import { tokenAccessor } from "core/model/api/transport";
import { getBackendConfig } from "core/model/config";
import { QueryProgress } from "core/types";
import { Banner, Spinner } from "ds/ui";
import { useEffect, useState } from "react";
import { useTranslations } from "translations";

type TranscriptSegment = {
  speaker?: string | null;
  text?: string | null;
};

type TranscriptData = {
  language?: string | null;
  segments: TranscriptSegment[];
};

function isTranscriptData(data: unknown): data is TranscriptData {
  return (
    typeof data === "object" &&
    data !== null &&
    "segments" in data &&
    Array.isArray((data as TranscriptData).segments)
  );
}

function SegmentsViewer({ data }: { data: TranscriptData }) {
  return (
    <div className="flex flex-col gap-4 overflow-y-auto px-6 py-4">
      {data.segments.map((segment, index) => (
        <div key={index} className="flex flex-col gap-0.5">
          {segment.speaker ? (
            <span className="text-body-sm font-bold text-gray-600">
              {segment.speaker}
            </span>
          ) : null}
          <p className="text-body-md whitespace-pre-wrap">{segment.text}</p>
        </div>
      ))}
    </div>
  );
}

/**
 * Renders a voice transcript from its processed document URI. Consultation
 * transcripts arrive as diarised JSON segments; dictation transcripts arrive as
 * a formatted markdown/plain-text transcript. Both are read-only.
 */
export function VoiceTranscriptViewer({ url }: { url: string }) {
  const translations = useTranslations();
  const aiCoreConfig = getBackendConfig(BACKEND.AI_CORE);
  const token = tokenAccessor();
  const [transcript, setTranscript] = useState<TranscriptData | null>(null);
  const [rawText, setRawText] = useState<string | null>(null);
  const [progress, setProgress] = useState<QueryProgress>(
    QUERY_PROGRESS_NOT_STARTED,
  );

  useEffect(() => {
    if (!url) {
      setTranscript(null);
      setRawText(null);
      setProgress(QUERY_PROGRESS_NOT_STARTED);
      return;
    }

    let cancelled = false;
    setProgress(QUERY_PROGRESS_PENDING);
    setTranscript(null);
    setRawText(null);

    const isAiCoreHostedFile =
      !!aiCoreConfig?.host && url.includes(aiCoreConfig.host);
    const headers: HeadersInit = {};
    if (isAiCoreHostedFile && token) {
      headers.Authorization = `Bearer ${token}`;
    }

    fetch(url, { headers })
      .then(async (res) => {
        if (!res.ok) {
          console.error(
            "No fetch response in VoiceTranscriptViewer:",
            `${res.status} ${res.statusText}`,
          );
          setProgress(QUERY_PROGRESS_FAILED);
        }
        return res.text();
      })
      .then((text) => {
        if (cancelled) return;
        try {
          const parsed = JSON.parse(text);
          if (isTranscriptData(parsed)) {
            setTranscript(parsed);
          } else if (typeof parsed === "string") {
            setRawText(parsed);
          } else {
            setRawText(JSON.stringify(parsed, null, 2));
          }
        } catch {
          setRawText(text);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setProgress(QUERY_PROGRESS_FAILED);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setProgress(QUERY_PROGRESS_SUCCEED);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [url, aiCoreConfig?.host, token]);

  if (progress === QUERY_PROGRESS_FAILED) {
    return (
      <Banner
        color="danger"
        message={translations.extractDocs.splitView.sourcesTab.errorNoHisData}
      />
    );
  }

  if (isLoading(progress)) {
    return (
      <div className="flex flex-1 items-center justify-center py-12">
        <Spinner
          color="primary"
          size="lg"
          testId="spinner-loading-voice-transcript"
        />
      </div>
    );
  }

  if (transcript) {
    return <SegmentsViewer data={transcript} />;
  }

  return (
    <pre className="overflow-auto px-6 py-4 text-left font-sans text-body-md whitespace-pre-wrap">
      {rawText ?? ""}
    </pre>
  );
}
